/**
 * ============================================================================
 * GLOSSARY TAB — last tab in the tab bar.
 * ============================================================================
 *
 *
 * Thin composition of shared primitives (the pattern all tabs should follow):
 *   QueryStateGuard (loading/error) → SectionCard (chrome) → DataTable (grid)
 * All this file owns is: which query to run, column configs, and layout order.
 *
 * TABLE BEHAVIOUR (matches the legacy dashboard):
 * - singleLine: every row is one line; overflowing text gets an ellipsis and
 *   the full text shows in a native tooltip on hover.
 * - maxVisibleRows=20: at most 20 rows visible; more scroll within the table
 *   under a sticky header. Shorter tables size to their content.
 *
 * DATA: served on getMetricDefinitions (spec hilt-wbr-api-serving) —
 * definitions carry description/reportingBasis/example, the response carries
 * glossaryTerms. selectGlossary reshapes the catalog into the two tables.
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Input from '@amzn/awsui-components-core/input';
import {
  DataTable,
  DataTableColumn,
  ExportColumn,
  ExportMenu,
  QueryStateGuard,
  SectionCard,
} from 'src/components/common';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import {
  normalizeMetricName,
  selectGlossary,
} from 'src/features/api/glossarySelectors';
import { useAppSelector } from 'src/app/store';
import { WBR_REVIEW_TYPE } from 'src/constants';
import { GlossaryTerm, MetricDefinition } from 'src/types';
import './GlossaryTab.scss';

const MAX_VISIBLE_ROWS = 12;

/**
 * Percentage widths so both tables fit the container and WRAP their text —
 * a reference page is for READING, so the QuickSight-import treatment
 * (one clipped line + hover tooltip + sideways scroll) is gone. The name
 * and term columns render emphasized; definitions/examples wrap freely.
 */
const metricDefinitionColumns: Array<DataTableColumn<MetricDefinition>> = [
  {
    id: 'order',
    header: '#',
    width: '4%',
    render: (m) => m.order,
    sortValue: (m) => m.order,
  },
  {
    id: 'name',
    header: 'Metric Name',
    width: '18%',
    render: (m) => <span className="glossary-name">{m.metricName}</span>,
    sortValue: (m) => m.metricName,
  },
  {
    id: 'basis',
    header: 'Reporting Basis',
    width: '16%',
    render: (m) => m.reportingBasis,
  },
  {
    id: 'definition',
    header: 'Definition',
    width: '30%',
    render: (m) => m.definition,
  },
  {
    id: 'example',
    header: 'Example',
    width: '32%',
    render: (m) => <span className="glossary-example">{m.example}</span>,
    // render returns a node, so sorting needs the raw text (same reason
    // name/term carry sortValue) — else it compares React elements.
    sortValue: (m) => m.example,
  },
];

const glossaryTermColumns: Array<DataTableColumn<GlossaryTerm>> = [
  {
    id: 'category',
    header: 'Category',
    width: '16%',
    render: (t) => t.category,
    sortValue: (t) => t.category,
  },
  {
    id: 'term',
    header: 'Term',
    width: '26%',
    render: (t) => <span className="glossary-name">{t.term}</span>,
    sortValue: (t) => t.term,
  },
  {
    id: 'description',
    header: 'Description',
    width: '58%',
    render: (t) => t.description,
  },
];

/** Case-insensitive substring match across the row's searchable fields. */
function matches(needle: string, ...fields: Array<string | number>): boolean {
  const q = needle.toLowerCase();
  return fields.some((f) => String(f).toLowerCase().includes(q));
}

/** Export variants: raw text per cell (see exportTable.ts contract). */
const metricDefinitionExportColumns: Array<ExportColumn<MetricDefinition>> = [
  { header: '#', value: (m) => m.order },
  { header: 'Metric Name', value: (m) => m.metricName },
  { header: 'Reporting Basis', value: (m) => m.reportingBasis },
  { header: 'Definition', value: (m) => m.definition },
  { header: 'Example', value: (m) => m.example },
];

const glossaryTermExportColumns: Array<ExportColumn<GlossaryTerm>> = [
  { header: 'Category', value: (t) => t.category },
  { header: 'Term', value: (t) => t.term },
  { header: 'Description', value: (t) => t.description },
];

export const GlossaryTab: React.FC = () => {
  const dashboardId = useAppSelector((s) => s.dashboard.activeDashboardId);
  const query = useGetMetricDefinitionsQuery({
    dashboardId,
    reviewType: WBR_REVIEW_TYPE,
  });
  // Deep link from the metric tooltips (spec metric-definition-tooltips
  // R2.4): ?term=<normalized name> highlights and scrolls to that entry so
  // the reader never scans the table by hand.
  const [searchParams, setSearchParams] = useSearchParams();
  const term = searchParams.get('term');
  // One search across BOTH tables (client-side; the glossary is small and
  // static). Ephemeral by design — never in the URL, resets on tab leave.
  const [search, setSearch] = React.useState('');

  // The highlight is a "here it is" locator, not a selection: the first
  // click anywhere OUTSIDE the highlighted row dismisses it and drops ?term=
  // from the URL (history replace — Back stays clean, refresh does not
  // resurrect it). Clicking ON the row keeps it, since that's the reader
  // engaging with the very entry the link pointed at (text selection, copy).
  React.useEffect(() => {
    if (!term) return undefined;
    const dismiss = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.('tr.data-table__row--highlight')) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('term');
          return next;
        },
        { replace: true },
      );
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [term, setSearchParams]);

  return (
    <QueryStateGuard
      query={query}
      errorText="Failed to load glossary data. Please try again later."
    >
      {(catalog) => {
        // The catalog IS the glossary source (spec hilt-wbr-api-serving);
        // reshape it into the two tables the page renders.
        const data = selectGlossary(catalog);
        const highlighted = term
          ? data.metricDefinitions.find(
              (m) => normalizeMetricName(m.metricName) === term,
            )
          : undefined;
        const definitions = search
          ? data.metricDefinitions.filter((m) =>
              matches(
                search,
                m.metricName,
                m.reportingBasis,
                m.definition,
                m.example,
              ),
            )
          : data.metricDefinitions;
        const terms = search
          ? data.terms.filter((t) =>
              matches(search, t.category, t.term, t.description),
            )
          : data.terms;
        const count = (shown: number, total: number) =>
          search ? `(${shown} of ${total})` : `(${total})`;
        return (
          <div className="glossary-tab">
            {/* One search over both tables — a reference page's first need. */}
            <div className="glossary-tab__search">
              <Input
                type="search"
                value={search}
                onChange={({ detail }) => setSearch(detail.value)}
                placeholder="Search metric definitions and terms"
                clearAriaLabel="Clear search"
                ariaLabel="Search glossary"
              />
            </div>

            <SectionCard
              title={`Metric Definitions ${count(
                definitions.length,
                data.metricDefinitions.length,
              )}`}
              actions={
                <ExportMenu
                  columns={metricDefinitionExportColumns}
                  rows={definitions}
                  filename="metric-definitions"
                />
              }
            >
              <DataTable
                columns={metricDefinitionColumns}
                items={definitions}
                rowKey={(m) => String(m.order)}
                headerTheme="blue"
                maxVisibleRows={MAX_VISIBLE_ROWS}
                emptyText={`No metric definitions match "${search}".`}
                highlightRowKey={
                  highlighted ? String(highlighted.order) : undefined
                }
              />
            </SectionCard>

            <SectionCard
              title={`Glossary - Filters, Tabs and Terms ${count(
                terms.length,
                data.terms.length,
              )}`}
              actions={
                <ExportMenu
                  columns={glossaryTermExportColumns}
                  rows={terms}
                  filename="glossary-terms"
                />
              }
            >
              <DataTable
                columns={glossaryTermColumns}
                items={terms}
                rowKey={(t) => `${t.category}|${t.term}`}
                headerTheme="navy"
                maxVisibleRows={MAX_VISIBLE_ROWS}
                emptyText={`No glossary terms match "${search}".`}
              />
            </SectionCard>
          </div>
        );
      }}
    </QueryStateGuard>
  );
};
